/** The apps' building blocks. Styles are in base.css. */
import {
  type ButtonHTMLAttributes,
  createContext,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { initials } from "./format";
import {
  AlertIcon,
  CheckIcon,
  ChevronLeft,
  ChevronRight,
  CloseIcon,
  MinusIcon,
  PlusIcon,
} from "./icons";
import { goBack } from "./router";

export function Button({
  variant = "primary",
  size,
  loading = false,
  block = true,
  value,
  children,
  disabled,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "small";
  loading?: boolean;
  block?: boolean;
  /** Shown on the right, e.g. the price: the words go on the left. */
  value?: ReactNode;
}) {
  const classes = [
    "btn",
    `btn-${variant}`,
    block && "btn-block",
    size && `btn-${size}`,
    value !== undefined && !loading && "btn-split",
    className,
  ];
  return (
    <button
      type="button"
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes.filter(Boolean).join(" ")}
    >
      {loading ? (
        <Spinner small />
      ) : value !== undefined ? (
        <>
          <span className="btn-label">{children}</span>
          <span className="btn-value">{value}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

export function IconButton({
  label,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...props}
      className={`icon-btn ${className}`}
    >
      {children}
    </button>
  );
}

export function Spinner({ small = false }: { small?: boolean }) {
  return (
    <span
      className={`spinner${small ? " spinner-small" : ""}`}
      role="status"
      aria-label="Loading"
    />
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading">
      <Spinner />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function TopBar({
  title,
  back,
  right,
  transparent = false,
}: {
  title?: string;
  /** Where the back arrow goes if the app wasn't opened on this screen's way in. */
  back?: string;
  right?: ReactNode;
  transparent?: boolean;
}) {
  // Tab screens (nowhere to go back to) get a big title on the left.
  const large = back === undefined && !transparent;
  return (
    <header
      className={`top-bar${transparent ? " top-bar-transparent" : ""}${large ? " top-bar-large" : ""}`}
    >
      {back !== undefined ? (
        <IconButton label="Back" onClick={() => goBack(back)} className="top-bar-back">
          <ChevronLeft />
        </IconButton>
      ) : (
        <span className="top-bar-spacer" />
      )}
      {title && <h1 className="top-bar-title">{title}</h1>}
      <span className="top-bar-right">{right}</span>
    </header>
  );
}

export function Field({
  label,
  hint,
  error,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string | null;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} aria-invalid={error ? true : undefined} {...props} />
      {error ? (
        <p className="field-error">{error}</p>
      ) : hint ? (
        <p className="field-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextArea({
  label,
  hint,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <textarea id={id} rows={2} {...props} />
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

export function Stepper({
  label,
  value,
  min,
  max,
  onChange,
  icon,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  icon?: ReactNode;
}) {
  return (
    <div className="stepper">
      {icon && <span className="stepper-icon">{icon}</span>}
      <span className="stepper-label">{label}</span>
      <IconButton
        label={`Fewer ${label.toLowerCase()}`}
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
      >
        <MinusIcon size={18} />
      </IconButton>
      <output className="stepper-value" aria-live="polite">
        {value}
      </output>
      <IconButton
        label={`More ${label.toLowerCase()}`}
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
      >
        <PlusIcon size={18} />
      </IconButton>
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  const name = useId();
  return (
    <fieldset className="segmented">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <label key={option.value} className={option.value === value ? "active" : ""}>
          <input
            type="radio"
            className="sr-only"
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
}

/** One of a set of options, shown as a card with a tick (a radio button underneath). */
export function Choice({
  name,
  checked,
  onSelect,
  icon,
  title,
  subtitle,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
}) {
  return (
    <label className={`choice${checked ? " active" : ""}`}>
      <input type="radio" className="sr-only" name={name} checked={checked} onChange={onSelect} />
      <span className="choice-icon">{icon}</span>
      <span className="choice-text">
        <strong>{title}</strong>
        {subtitle && <span>{subtitle}</span>}
      </span>
      <span className="choice-check">{checked && <CheckIcon size={18} />}</span>
    </label>
  );
}

export function ChoiceGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <fieldset className="choice-list">
      <legend className="sr-only">{label}</legend>
      {children}
    </fieldset>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Row({
  icon,
  title,
  subtitle,
  right,
  onClick,
  chevron = Boolean(onClick),
  danger = false,
}: {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  danger?: boolean;
}) {
  const content = (
    <>
      {icon && <span className="row-icon">{icon}</span>}
      <span className="row-text">
        <span className="row-title">{title}</span>
        {subtitle && <span className="row-subtitle">{subtitle}</span>}
      </span>
      {right && <span className="row-right">{right}</span>}
      {chevron && <ChevronRight size={18} className="row-chevron" />}
    </>
  );
  return onClick ? (
    <button
      type="button"
      className={`row row-button${danger ? " row-danger" : ""}`}
      onClick={onClick}
    >
      {content}
    </button>
  ) : (
    <div className="row">{content}</div>
  );
}

export function Notice({
  tone = "info",
  children,
  action,
}: {
  tone?: "info" | "warning" | "error" | "success";
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`notice notice-${tone}`} role={tone === "error" ? "alert" : "status"}>
      {tone !== "info" && tone !== "success" && <AlertIcon size={18} />}
      <div className="notice-body">{children}</div>
      {action}
    </div>
  );
}

/**
 * Where something's up to: a bar for each step, filled up to the current one (which
 * moves). Past the last step, they're all done.
 */
export function Steps({ steps, current }: { steps: string[]; current: number }) {
  const label =
    current >= steps.length
      ? `All ${steps.length} steps done`
      : `Step ${current + 1} of ${steps.length}: ${steps[current]}`;
  return (
    <ol className="steps" aria-label={label}>
      {steps.map((step, i) => (
        <li
          key={step}
          className={i < current ? "done" : i === current ? "current" : undefined}
          aria-hidden="true"
        >
          {step}
        </li>
      ))}
    </ol>
  );
}

/** A UK number plate. */
export function Plate({ registration }: { registration: string }) {
  return <span className="plate">{registration.toUpperCase()}</span>;
}

export function Avatar({
  name,
  photoUrl,
  size = 52,
}: {
  name: string;
  photoUrl?: string | null;
  size?: number;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {photoUrl && !failed ? (
        <img src={photoUrl} alt="" onError={() => setFailed(true)} />
      ) : (
        initials(name) || "?"
      )}
    </span>
  );
}

export function Empty({
  icon,
  title,
  children,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <h2>{title}</h2>
      {children}
    </div>
  );
}

/** A sheet that slides up over the screen, for choices and confirmations. */
export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: tapping outside is extra; Escape closes dialogs anyway.
    <dialog
      ref={ref}
      className="sheet"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      aria-label={title}
    >
      <div className="sheet-body">
        <div className="sheet-grabber" aria-hidden="true" />
        <div className="sheet-header">
          <h2>{title}</h2>
          <IconButton label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        {children}
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Toasts: short messages at the bottom of the screen.
// ---------------------------------------------------------------------------

type Toast = { id: number; message: string; tone: "info" | "error" };
const ToastContext = createContext<(message: string, tone?: Toast["tone"]) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((message: string, tone: Toast["tone"] = "info") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current.slice(-2), { id, message, tone }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 4500);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.tone}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
